import { useState, useEffect, FC, Fragment } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  makeStyles,
  TablePagination,
  Collapse,
  Link,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import VisibilityIcon from '@material-ui/icons/Visibility';
import RefreshIcon from '@material-ui/icons/Refresh';
import CodeIcon from '@material-ui/icons/Code';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import {
  CheckCircleIcon,
  TimesCircleIcon,
  SyncAltIcon,
  CogIcon,
  BanIcon,
  CircleIcon,
} from '@patternfly/react-icons';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';
import { GitLabPipelineVisualization } from './topology/GitLabPipelineVisualization';
import type { GitLabPipeline } from './topology/gitlab-topology-utils';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  footer: {
    '&:nth-of-type(odd)': {
      backgroundColor: `${theme.palette.background.paper}`,
    },
  },
  statusChip: {
    fontWeight: 'bold',
  },
  successChip: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  failedChip: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  runningChip: {
    backgroundColor: '#2196f3',
    color: 'white',
  },
  pendingChip: {
    backgroundColor: '#ff9800',
    color: 'white',
  },
  canceledChip: {
    backgroundColor: '#9e9e9e',
    color: 'white',
  },
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    padding: theme.spacing(2),
    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
    fontSize: '0.875rem',
    maxHeight: 500,
    overflow: 'auto',
    borderRadius: theme.shape.borderRadius,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  jobAccordion: {
    marginBottom: theme.spacing(1),
  },
  jobHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
  },
  jobInfo: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
  dialogContent: {
    minHeight: 400,
  },
  refreshButton: {
    marginLeft: theme.spacing(2),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  gapBox: {
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
}));

// GitLab API 类型定义已移至 topology/gitlab-topology-utils.ts

// 主组件 - GitLab Pipeline Runs
export const GitLabPipelineRuns: FC = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  
  const [pipelines, setPipelines] = useState<GitLabPipeline[]>([]);
  const [loading, setLoading] = useState(true);
  
  // 分页状态 - 复用 Tekton 的配置
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  
  // 展开状态 - 记录哪些行被展开
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  
  // Jobs 数量缓存 - 记录每个 Pipeline 的 Jobs 数量
  const [jobsCounts, setJobsCounts] = useState<Record<number, number>>({});
  
  // Jobs 信息缓存 - 记录每个 Pipeline 的 Jobs 信息（stage 和 status）
  const [jobsInfo, setJobsInfo] = useState<Record<number, Array<{stage: string, status: string}>>>({});

  // 获取项目路径 - 兼容多种注解格式
  const projectId = entity?.metadata?.annotations?.['gitlab.com/project-id'] || 
                   entity?.metadata?.annotations?.['gitlab.com/project_id'] || '';
  const projectSlug = entity?.metadata?.annotations?.['gitlab.com/project-slug'] || 
                     entity?.metadata?.annotations?.['gitlab.com/project_slug'] || '';
  const projectPath = projectId || projectSlug;
  const gitlabInstance = entity?.metadata?.annotations?.['gitlab.com/instance'] || 'git.ekuaibao.com';
  
  // 当项目路径为空时的提示
  // (已移除调试信息，生产环境不需要)

  useEffect(() => {
    const fetchPipelines = async () => {
      if (!projectPath) {
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // 获取后端基础 URL
        const backendUrl = await discoveryApi.getBaseUrl('');
        
        // 获取 Backstage 认证 token
        const { token } = await identityApi.getCredentials();
        
        // 使用 GitLab 插件的代理 API
        const apiUrl = `${backendUrl}gitlab/rest/git.ekuaibao.com/projects/${encodeURIComponent(projectPath)}/pipelines?per_page=20`;
        
        const response = await fetch(apiUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          
          // 为每个 Pipeline 从第一个 Job 获取 commit 信息
          const pipelinesWithCommit = await Promise.all(
            (data || []).map(async (pipeline: GitLabPipeline) => {
              // 如果 pipeline 中已经有完整的 commit 信息，直接返回
              if (pipeline.commit?.author_name || pipeline.commit?.committer_name) {
                return pipeline;
              }
              
              // 从 jobs API 获取 commit 信息（jobs API 会返回完整的 commit 数据）
              try {
                const jobsResponse = await fetch(
                  `${backendUrl}gitlab/rest/git.ekuaibao.com/projects/${encodeURIComponent(projectPath)}/pipelines/${pipeline.id}/jobs?per_page=1`,
                  {
                    headers: {
                      'Authorization': `Bearer ${token}`,
                    },
                  }
                );
                if (jobsResponse.ok) {
                  const jobs = await jobsResponse.json();
                  if (jobs && jobs.length > 0 && jobs[0].commit) {
                    // 使用第一个 job 的 commit 信息
                    return { 
                      ...pipeline, 
                      commit: jobs[0].commit,
                      user: jobs[0].user // 也更新 user 信息
                    };
                  }
                }
              } catch (err) {
                // 静默失败
              }
              return pipeline;
            })
          );
          
          setPipelines(pipelinesWithCommit);
        } else {
          const errorText = await response.text();
          throw new Error(`API 错误: ${response.status} - ${errorText}`);
        }
      } catch (error) {
        // 静默失败，显示空列表
        setPipelines([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPipelines();
  }, [projectPath, identityApi, discoveryApi]);

  // 分页处理函数 - 复用 Tekton 的实现
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // 计算可见行 - 复用 Tekton 的逻辑
  const visibleRows = pipelines.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  // 计算空行 - 避免布局跳动
  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - pipelines.length)
      : 0;

  // 为当前页的 Pipelines 获取 jobs 信息
  useEffect(() => {
    const fetchJobsForVisibleRows = async () => {
      if (visibleRows.length === 0) return;

      const token = (await identityApi.getCredentials()).token;

      // 并行获取当前页所有 Pipelines 的 jobs
      const backendUrl = await discoveryApi.getBaseUrl('');
      
      const jobsPromises = visibleRows.map(async (pipeline) => {
        try {
          const response = await fetch(
            `${backendUrl}gitlab/rest/git.ekuaibao.com/projects/${encodeURIComponent(projectPath)}/pipelines/${pipeline.id}/jobs`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );
          if (response.ok) {
            const jobs = await response.json();
            // 按创建时间排序
            const sortedJobs = (jobs || []).sort((a: any, b: any) => {
              return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
            });
            return {
              pipelineId: pipeline.id,
              count: sortedJobs.length,
              info: sortedJobs.map((job: any) => ({
                stage: job.stage,
                status: job.status,
              })),
            };
          }
        } catch (error) {
          // 忽略错误
        }
        return null;
      });

      const results = await Promise.all(jobsPromises);
      
      // 更新状态
      const newCounts: Record<number, number> = {};
      const newInfo: Record<number, Array<{stage: string, status: string}>> = {};
      
      results.forEach((result) => {
        if (result) {
          newCounts[result.pipelineId] = result.count;
          newInfo[result.pipelineId] = result.info;
        }
      });
      
      setJobsCounts(prev => ({ ...prev, ...newCounts }));
      setJobsInfo(prev => ({ ...prev, ...newInfo }));
    };

    fetchJobsForVisibleRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, pipelines.length, projectPath]);

  const handleRefresh = async () => {
    if (!projectPath) return;
    
    setLoading(true);
    try {
      // 获取后端基础 URL
      const backendUrl = await discoveryApi.getBaseUrl('');
      
      // 获取 Backstage 认证 token
      const { token } = await identityApi.getCredentials();
      
      const response = await fetch(`${backendUrl}gitlab/rest/git.ekuaibao.com/projects/${encodeURIComponent(projectPath)}/pipelines?per_page=20`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        
        // 为每个 Pipeline 从第一个 Job 获取 commit 信息
        const pipelinesWithCommit = await Promise.all(
          (data || []).map(async (pipeline: GitLabPipeline) => {
            if (pipeline.commit?.author_name || pipeline.commit?.committer_name) {
              return pipeline;
            }
            
            try {
              const jobsResponse = await fetch(
                `${backendUrl}gitlab/rest/git.ekuaibao.com/projects/${encodeURIComponent(projectPath)}/pipelines/${pipeline.id}/jobs?per_page=1`,
                {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                  },
                }
              );
              if (jobsResponse.ok) {
                const jobs = await jobsResponse.json();
                if (jobs && jobs.length > 0 && jobs[0].commit) {
                  return { 
                    ...pipeline, 
                    commit: jobs[0].commit,
                    user: jobs[0].user
                  };
                }
              }
            } catch {
              // 静默失败
            }
            return pipeline;
          })
        );
        
        setPipelines(pipelinesWithCommit);
      } else {
        throw new Error(`API 错误: ${response.status}`);
      }
    } catch (error) {
      // 静默失败
    } finally {
      setLoading(false);
    }
  };

  const getStatusChipClass = (status: string) => {
    const statusMap: { [key: string]: string } = {
      success: classes.successChip,
      failed: classes.failedChip,
      running: classes.runningChip,
      pending: classes.pendingChip,
      canceled: classes.canceledChip,
      skipped: classes.canceledChip,
    };
    return `${classes.statusChip} ${statusMap[status?.toLowerCase()] || ''}`;
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`;
    }
    return `${secs}s`;
  };

  // 计算 Pipeline 持续时间（基于 created_at 和 updated_at）
  const calculateDuration = (pipeline: GitLabPipeline): number | undefined => {
    if (pipeline.duration) return pipeline.duration;
    
    // 如果 API 没返回 duration，用 updated_at - created_at 计算
    const created = new Date(pipeline.created_at).getTime();
    const updated = new Date(pipeline.updated_at).getTime();
    return Math.round((updated - created) / 1000);
  };

  // 获取状态中文标签
  const getStatusLabel = (status: string): string => {
    if (status === 'success') return '已通过';
    if (status === 'failed') return '已失败';
    return status;
  };

  // 渲染 Jobs 状态圆圈 - 参考 GitLab 的阶段显示（带连接线）
  const renderJobsStages = (pipelineId: number) => {
    const jobs = jobsInfo[pipelineId] || [];
    if (jobs.length === 0) {
      return <Typography variant="caption">-</Typography>;
    }

    return (
      <Box display="flex" alignItems="center" flexWrap="wrap">
        {jobs.map((job, index) => {
          const { stage, status } = job;
          let bgColor = '#e0e0e0';
          let iconColor = '#9e9e9e';
          let IconComponent = CircleIcon;
          
          if (status === 'success') {
            bgColor = '#c8e6c9';
            iconColor = '#4caf50';
            IconComponent = CheckCircleIcon;
          } else if (status === 'failed') {
            bgColor = '#ffcdd2';
            iconColor = '#f44336';
            IconComponent = TimesCircleIcon;
          } else if (status === 'running') {
            bgColor = '#bbdefb';
            iconColor = '#2196f3';
            IconComponent = SyncAltIcon;
          } else if (status === 'manual') {
            // manual = 等待手动触发，显示灰色设置图标
            bgColor = '#e0e0e0';
            iconColor = '#9e9e9e';
            IconComponent = CogIcon;
          } else if (status === 'canceled' || status === 'skipped') {
            bgColor = '#e0e0e0';
            iconColor = '#9e9e9e';
            IconComponent = BanIcon;
          } else if (status === 'pending' || status === 'created') {
            bgColor = '#e0e0e0';
            iconColor = '#9e9e9e';
            IconComponent = CircleIcon;
          }
          
          return (
            <Box key={index} display="flex" alignItems="center">
              <Tooltip title={`${stage}: ${status}`}>
                <Box
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    backgroundColor: bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconComponent style={{ fontSize: '14px', color: iconColor }} />
                </Box>
              </Tooltip>
              {/* 连接线 - 最后一个 job 不显示 */}
              {index < jobs.length - 1 && (
                <Box
                  style={{
                    width: '12px',
                    height: '2px',
                    backgroundColor: '#d0d0d0',
                    margin: '0 2px',
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  if (!projectPath) {
    return (
      <Alert severity="warning">
        <Typography variant="body1">
          未配置 GitLab 项目信息
        </Typography>
        <Typography variant="body2">
          请在 catalog-info.yaml 中添加 gitlab.com/project-id 或 gitlab.com/project-slug 注解
        </Typography>
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (pipelines.length === 0) {
    return (
      <Card>
        <CardContent className={classes.emptyState}>
          <Typography variant="h1" style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }}>
            🔧
          </Typography>
          <Typography variant="h6" gutterBottom>
            暂无 Pipeline 运行记录
          </Typography>
          <Typography variant="body2" color="textSecondary">
            当有新的 Pipeline 运行时，将在这里显示
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            style={{ marginTop: 16 }}
          >
            刷新
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={classes.root}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" style={{ fontSize: '20px', fontWeight: 'bold' }}>
          CI：GitLab Pipeline Runs
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          className={classes.refreshButton}
        >
          刷新
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 50 }} />
              <TableCell>状态</TableCell>
              <TableCell>流水线</TableCell>
              <TableCell>创建人</TableCell>
              <TableCell>阶段</TableCell>
              <TableCell align="center">操作</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map(pipeline => {
              const isExpanded = expandedRows[pipeline.id] || false;
              return (
              <Fragment key={pipeline.id}>
              <TableRow hover>
                {/* 展开按钮 */}
                <TableCell>
                  <IconButton
                    aria-label="expand row"
                    size="small"
                    onClick={() => {
                      setExpandedRows(prev => ({
                        ...prev,
                        [pipeline.id]: !prev[pipeline.id],
                      }));
                    }}
                  >
                    {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                  </IconButton>
                </TableCell>
                
                {/* 状态列 - 持续时间和完成时间 */}
                <TableCell>
                  <Box display="flex" alignItems="center" style={{ marginBottom: '4px' }}>
                    <Chip
                      size="small"
                      label={getStatusLabel(pipeline.status)}
                      className={getStatusChipClass(pipeline.status)}
                      style={{ height: '22px', fontSize: '0.75rem' }}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', fontSize: '0.7rem', marginBottom: '2px' }}>
                      ⏱ {formatDuration(calculateDuration(pipeline))}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" style={{ display: 'block', fontSize: '0.7rem' }}>
                      📅 {new Date(pipeline.updated_at).toLocaleString('zh-CN')}
                    </Typography>
                  </Box>
                </TableCell>
                
                {/* 流水线列 */}
                <TableCell>
                  {/* 第一行：Commit 标题（带链接） */}
                  <Box mb={0.5}>
                    <Link
                      href={`https://${gitlabInstance}/${projectSlug}/-/commit/${pipeline.sha}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                    >
                      {pipeline.commit?.title || pipeline.sha.substring(0, 8)}
                    </Link>
                  </Box>
                  {/* 第二行：Pipeline ID + 分支 + Commit SHA（都带链接） */}
                  <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    <Link
                      href={pipeline.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      #{pipeline.id}
                    </Link>
                    <CodeIcon fontSize="small" style={{ fontSize: '14px' }} />
                    <Link
                      href={`https://${gitlabInstance}/${projectSlug}/-/commits/${encodeURIComponent(pipeline.ref)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pipeline.ref}
                    </Link>
                    <Link
                      href={`https://${gitlabInstance}/${projectSlug}/-/commit/${pipeline.sha}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Typography variant="caption" style={{ fontFamily: 'monospace' }}>
                        {pipeline.sha.substring(0, 8)}
                      </Typography>
                    </Link>
                  </Box>
                </TableCell>
                
                {/* 创建人 - commit 作者头像 */}
                <TableCell>
                  {(() => {
                    // 优先显示 commit 作者，降级到 user，最后才是 System
                    const name = pipeline.commit?.author_name || 
                                 pipeline.commit?.committer_name || 
                                 pipeline.user?.name || 
                                 'System';
                    const avatar = pipeline.user?.avatar_url || '';
                    
                    return (
                      <Tooltip title={name}>
                        <Avatar
                          src={avatar}
                          style={{ width: 32, height: 32 }}
                        >
                          {name[0]?.toUpperCase()}
                        </Avatar>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                
                {/* 阶段 - Jobs 状态圆圈 */}
                <TableCell>
                  {renderJobsStages(pipeline.id)}
                </TableCell>
                
                {/* 操作 */}
                <TableCell align="center">
                  <Tooltip title="在 GitLab 中查看">
                    <IconButton
                      size="small"
                      component="a"
                      href={pipeline.web_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
              
              {/* 展开区域 - 显示详细信息和 Jobs 拓扑图 */}
              <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box margin={2}>
                      {/* Pipeline 详细信息 - GitLab 原生三行格式 */}
                      <Box mb={2} pb={2} style={{ borderBottom: '1px solid #e0e0e0' }}>
                        {/* 第一行：Commit 标题 */}
                        <Box mb={0.5}>
                          <Typography variant="body2" component="span" style={{ fontWeight: 'bold' }}>
                            {pipeline.commit?.title || pipeline.sha.substring(0, 8)}
                          </Typography>
                        </Box>
                        
                        {/* 第二行：状态 + 作者 + commit + 时间 */}
                        <Box mb={0.5}>
                          <Chip
                            label={getStatusLabel(pipeline.status)}
                            size="small"
                            className={getStatusChipClass(pipeline.status)}
                            style={{ marginRight: 8 }}
                          />
                          <Typography variant="body2" color="textSecondary" component="span">
                            {pipeline.commit?.author_name || pipeline.commit?.committer_name || pipeline.user?.name || 'System'} 为提交{' '}
                          </Typography>
                          <Typography variant="body2" component="span" style={{ fontFamily: 'monospace' }}>
                            {pipeline.sha.substring(0, 8)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" component="span">
                            {' '}创建了流水线，{new Date(pipeline.created_at).toLocaleString('zh-CN')}, 已完成 {new Date(pipeline.updated_at).toLocaleString('zh-CN')}
                          </Typography>
                        </Box>
                        
                        {/* 第三行：分支 */}
                        <Box mb={0.5}>
                          <Typography variant="body2" color="textSecondary" component="span">
                            对于{' '}
                          </Typography>
                          <Link
                            href={`https://${gitlabInstance}/${projectSlug}/-/commits/${encodeURIComponent(pipeline.ref)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontWeight: 'bold' }}
                          >
                            {pipeline.ref}
                          </Link>
                        </Box>
                        
                        {/* 第四行：作业数 + 耗时 + 排队时间 */}
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            🔧 {jobsCounts[pipeline.id] || '-'}个作业 ⏱ {formatDuration(calculateDuration(pipeline))}, 已排队 {pipeline.started_at ? `${Math.round((new Date(pipeline.started_at).getTime() - new Date(pipeline.created_at).getTime()) / 1000)}秒` : '0秒'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      {/* Pipeline Jobs 流程图 */}
                      <GitLabPipelineVisualization 
                        pipeline={pipeline} 
                        projectPath={projectPath}
                        onJobsLoaded={(count, info) => {
                          setJobsCounts(prev => ({
                            ...prev,
                            [pipeline.id]: count,
                          }));
                          setJobsInfo(prev => ({
                            ...prev,
                            [pipeline.id]: info,
                          }));
                        }}
                      />
                    </Box>
                  </Collapse>
                </TableCell>
              </TableRow>
            </Fragment>
            );
            })}
            
            {/* 空行填充 - 避免布局跳动 */}
            {emptyRows > 0 && (
              <TableRow style={{ height: 53 * emptyRows }}>
                <TableCell colSpan={6} />
              </TableRow>
            )}
            
            {/* 分页控件 - 复用 Tekton 的配置 */}
            <TableRow className={classes.footer}>
              <TablePagination
                rowsPerPageOptions={[
                  { value: 5, label: '5 rows' },
                  { value: 10, label: '10 rows' },
                  { value: 20, label: '20 rows' },
                ]}
                count={pipelines.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={null}
              />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};